import React from "react";
import { useState, useRef, useMemo } from "react";
import {
    Box, Typography, Avatar, Chip, Button, IconButton, Grid, TextField,
    Stack, Popper, Grow, Paper, ClickAwayListener, MenuItem, FormControl
} from "@mui/material";
import { FaUserCircle } from "react-icons/fa";
import { CiCalendar } from "react-icons/ci";
import moment from "moment";
import TableComponent from "components/grid";
import StatusBadge from "components/badge/StatusBadge";
import ProjectIcon2 from "assets/icons/ProjectIcon2.svg"
import ProjectIcon4 from "assets/icons/ProjectIcon4.svg"
import AeroplaneIcon from "assets/icons/ProjectIcon1.svg"
import HeartIcon from "assets/icons/HeartIcon.svg"
import ProjectIcon3 from "assets/icons/ProjectIcon3.svg"
import CustomPagination from "components/CustomPagination";
import { ReactComponent as EditIcon } from "assets/icons/EditIcon.svg";
import { ReactComponent as DeleteOutlineIcon } from "assets/icons/DeleteOutlined.svg";
import { ReactComponent as LocationIcon } from "assets/icons/Location.svg";
import { LuEye, LuRefreshCw } from "react-icons/lu";
import { BiListUl } from "react-icons/bi";
import { MdOutlineFileDownload, MdOutlineFilterAlt } from "react-icons/md";
import Select from "react-select";


const iconList = [
    { name: "ProjectIcon2", src: ProjectIcon2 },
    { name: "ProjectIcon4", src: ProjectIcon4 },
    { name: "AeroplaneIcon", src: AeroplaneIcon },
    { name: "HeartIcon", src: HeartIcon },
    { name: "ProjectIcon3", src: ProjectIcon3 },
];

const AddedProjects = ({ data = [], handleEditClick, handleDeleteClick, customerChk }) => {
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(5);
    const [open, setOpen] = useState(false);
    const anchorRef = useRef(null);


    const [filters, setFilters] = useState({
        ProjectName: '',
        ProjectType: '',
        Admin: '',
    });

    const extractedCategories = Array.isArray(data) && data.length > 0
        ? [...new Set(data.map(item => item.projectType).filter(Boolean))]
        : [];
    const projectType = extractedCategories.map(category => ({
        value: category,
        label: category
    }));




    const [order, setOrder] = useState('asc');
    const [orderBy, setOrderBy] = useState('');


    const projectStatusOptions = [
        { value: 'active', label: 'Active' },
        { value: 'completed', label: 'Completed' },
        { value: 'pending', label: 'Pending' }
    ];

    const projectTypeOptions = [
        { value: 'construction', label: 'Construction' },
        { value: 'research', label: 'Research' },
        { value: 'development', label: 'Development' }
    ];


    const handleToggle = () => {
        setOpen((prevOpen) => !prevOpen);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleDeleteFilter = (filterType) => {
        setFilters(prev => ({
            ...prev,
            [filterType]: ''
        }));
    };

    const handleSelectChange = (selectedOption, filterType) => {
        const value = selectedOption ? selectedOption.value : '';
        setFilters(prev => ({
            ...prev,
            [filterType]: value
        }));
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleReset = () => {
        setFilters({
            ProjectName: '',
            ProjectType: '',
            Admin: '',

        });
    };

    const handleApplyFilters = () => {
        setCurrentPage(1);
        handleClose();
    };


    const handlePageChange = (newPage) => {
        setCurrentPage(newPage);
    };

    const handlePageSizeChange = (newSize) => {
        setPageSize(newSize);
        setCurrentPage(1);
    };


    const createSortHandler = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };


    const processedData = useMemo(() => {
        let result = [...data];


        if (filters.ProjectName) {
            result = result.filter(item =>
                item.projectName?.toLowerCase().includes(filters.ProjectName.toLowerCase())
            );
        }

        if (filters.ProjectType) {
            result = result.filter(item => item.projectType === filters.ProjectType);
        }

        if (filters.Admin) {
            result = result.filter(item =>
                item.projectManagerName?.toString().includes(filters.Admin)
            );
        }


        if (orderBy) {
            result = [...result].sort((a, b) => {
                const aValue = a[orderBy];
                const bValue = b[orderBy];

                if (typeof aValue === 'string') {
                    return order === 'asc'
                        ? aValue.localeCompare(bValue)
                        : bValue.localeCompare(aValue);
                }

                if (typeof aValue === 'number') {
                    return order === 'asc' ? aValue - bValue : bValue - aValue;
                }

                return 0;
            });
        }
        return result;
    }, [data, filters, order, orderBy]);


    const paginatedData = useMemo(() => {
        const startIndex = (currentPage - 1) * pageSize;
        const endIndex = startIndex + pageSize;
        return processedData.slice(startIndex, endIndex);
    }, [processedData, currentPage, pageSize]);

    const totalCount = processedData.length;


    const columns = [
        {
            name: "Name",
            field: "projectName",
            width: "360px",
            renderer: (row) => (
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-start", gap: "10px" }}>
                    <Avatar sx={{ width: 30, height: 30 }}>
                        {(() => {
                            const icon = iconList.find((i) => i.name === row.projectIcon);
                            return icon ? (
                                <img
                                    src={icon.src}
                                    alt={icon.name}
                                    width="30"
                                    height="30"
                                />
                            ) : null;
                        })()}
                    </Avatar>
                    <Box>
                        <Typography
                            sx={{
                                fontSize: "16px", fontWeight: 500, lineHeight: "100%", color: "#433F5E", fontFamily: "Outfit, sans-serif"
                            }}>
                            {row.projectName}
                        </Typography>
                        <Typography
                            sx={{
                                fontSize: "12px", fontWeight: 400, lineHeight: "100%", color: "#726F86", mt: "4px", fontFamily: "Outfit, sans-serif"
                            }}>
                            Type: {row.projectType}
                        </Typography>
                    </Box>
                    <Chip
                        sx={{
                            minWidth: "61px",
                            height: "17px",
                            borderRadius: "5px",
                            backgroundColor: "#E2F6FF",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: "2px 6px"
                        }}
                        label={
                            <Box sx={{
                                color: "#0A65E4",
                                fontWeight: 600,
                                fontSize: "10px",
                                lineHeight: 1,
                                fontFamily: "Outfit, sans-serif"
                            }}>
                                {row.status}
                            </Box>
                        }
                    />
                    {!customerChk && (
                        <>
                            <Box className="hoverButtons" sx={{ display: "none", gap: "10px" }}>
                                <Button
                                    onClick={() => handleEditClick(row)}
                                    startIcon={<EditIcon style={{ width: 11, height: 11, fill: "#433F5E" }} />}
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "4px",
                                        backgroundColor: "#E7E7EB",
                                        p: "2px 6px",
                                        borderRadius: "5px",
                                        boxShadow: "none",
                                        color: "#433F5E",
                                        textTransform: "none",
                                        fontSize: "11px",
                                        width: "48px",
                                        height: "18px",
                                        lineHeight: "100%",
                                        fontWeight: 600,
                                        fontFamily: "Outfit, sans-serif",
                                        "&:hover": {
                                            backgroundColor: "transparent",
                                            boxShadow: "none",
                                        },
                                    }}
                                >
                                    Edit
                                </Button>

                                <Button
                                    onClick={() => handleDeleteClick(row)}
                                    startIcon={<DeleteOutlineIcon style={{ width: 11, height: 11, fill: "#433F5E" }} />}
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "4px",
                                        backgroundColor: "#E7E7EB",
                                        p: "2px 6px",
                                        borderRadius: "5px",
                                        boxShadow: "none",
                                        color: "#433F5E",
                                        textTransform: "none",
                                        fontSize: "11px",
                                        width: "48px",
                                        height: "18px",
                                        lineHeight: "100%",
                                        fontWeight: 600,
                                        fontFamily: "Outfit, sans-serif",
                                        "&:hover": {
                                            backgroundColor: "transparent",
                                            boxShadow: "none",
                                        },
                                    }}
                                >
                                    Delete
                                </Button>
                            </Box>
                        </>
                    )}
                </Box>
            ),
        },
        {
            name: "Location",
            field: "country",
            // width: "12%",
            renderer: (row) => (
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-start", gap: "10px" }}>
                    <IconButton sx={{ width: 17, height: 17, p: 0 }}>
                        <LocationIcon
                            width={17}
                            height={17}
                            style={{ fill: "#A5A5A5" }}
                        />
                    </IconButton>
                    <Box>
                        <Typography
                            sx={{
                                fontSize: "16px", fontWeight: 400, lineHeight: "100%", color: "#433F5E", fontFamily: "Outfit, sans-serif"
                            }}>
                            {row.country}
                        </Typography>
                    </Box>
                </Box>
            ),
        },
        ...(!customerChk
            ? [
                {
                    name: "Budget",
                    field: "budgetedCost",
                    renderer: (row) => (
                        <Typography
                            sx={{
                                fontSize: "16px",
                                fontFamily: "Outfit, sans-serif",
                                fontWeight: 400,
                                lineHeight: "100%",
                                color: "#433F5E"
                            }}>
                            RS {row.budgetedCost} CR
                        </Typography>
                    ),
                },
            ]
            : []),
        {
            name: "Admin",
            field: "projectManagerName",
            // width: "12%",
            renderer: (row) => (
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-start", gap: "10px" }}>
                    <Avatar
                        sx={{
                            backgroundColor: "#ECECEC",
                            height: "32px",
                            width: "32px",
                            color: "#5F5F5F"
                        }}
                    >
                        {row.imagePath ? (
                            <img
                                src={`https://gr-api.ingencorp.in${row.imagePath}`}
                                alt="Admin Avatar"
                                style={{
                                    height: "32px",
                                    width: "32px",
                                    objectFit: "cover"
                                }}
                                onError={(e) => {

                                    e.target.style.display = 'none';
                                }}
                            />
                        ) : (
                            <FaUserCircle style={{ fontSize: 32, color: "#5F5F5F" }} />
                        )}
                    </Avatar>
                    <Box>
                        <Typography
                            sx={{
                                fontSize: "16px",
                                fontWeight: 500,
                                lineHeight: "155%",
                                color: "#433F5E",
                                fontFamily: "Outfit, sans-serif"
                            }}>
                            {row.projectManagerName}
                        </Typography>
                        <Typography
                            sx={{
                                fontSize: "12px",
                                fontWeight: 400,
                                lineHeight: "155%",
                                color: "#7E7E7E",
                                fontFamily: "Outfit, sans-serif"
                            }}>
                            {row.projectManagerDesignation}
                        </Typography>
                    </Box>
                </Box>
            ),
        }
    ];

    return (
        <>

            <Grid size={12}>
                <Grid container>
                    <Grid size={{ md: 8, sm: 6 }}>
                        <Stack
                            direction="row"
                            spacing={1}
                            sx={{
                                padding: "8px 0",
                                alignItems: "center",
                                minHeight: "40px"
                            }}
                        >

                            {filters.ProjectName && (
                                <Chip
                                    label={
                                        <Typography variant="body2">
                                            <strong style={{ color: '#0E73F6' }}>Project:</strong>{" "}
                                            <span style={{ fontWeight: 400, color: "#6B7280" }}>
                                                {filters.ProjectName}
                                            </span>
                                        </Typography>
                                    }
                                    sx={{
                                        backgroundColor: "rgba(25, 118, 210, 0.08)",
                                        border: 0
                                    }}
                                    variant="outlined"
                                    onDelete={() => handleDeleteFilter('ProjectName')}
                                />
                            )}

                            {filters.ProjectType && (
                                <Chip
                                    label={
                                        <Typography variant="body2">
                                            <strong style={{ color: '#0E73F6' }}>Type:</strong>{" "}
                                            <span style={{ fontWeight: 400, color: "#6B7280" }}>
                                                {projectTypeOptions.find(type => type.value === filters.ProjectType)?.label || filters.ProjectType}
                                            </span>
                                        </Typography>
                                    }
                                    sx={{
                                        backgroundColor: "rgba(25, 118, 210, 0.08)",
                                        border: 0
                                    }}
                                    variant="outlined"
                                    onDelete={() => handleDeleteFilter('ProjectType')}
                                />
                            )}

                            {filters.Admin && (
                                <Chip
                                    label={
                                        <Typography variant="body2">
                                            <strong style={{ color: '#0E73F6' }}>Admin:</strong>{" "}
                                            <span style={{ fontWeight: 400, color: "#6B7280" }}>
                                                {filters.Admin}
                                            </span>
                                        </Typography>
                                    }
                                    sx={{
                                        backgroundColor: "rgba(25, 118, 210, 0.08)",
                                        border: 0
                                    }}
                                    variant="outlined"
                                    onDelete={() => handleDeleteFilter('Admin')}
                                />
                            )}
                        </Stack>
                    </Grid>

                    <Grid size={{ md: 4, sm: 6 }}>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: 'end',
                                p: "8px 0px"
                            }}
                        >

                            <IconButton
                                ref={anchorRef}
                                onClick={handleToggle}
                            >
                                <MdOutlineFilterAlt size={24} color="#48535B" />
                            </IconButton>


                            <Popper
                                open={open}
                                anchorEl={anchorRef.current}
                                placement="bottom-end"
                                transition
                                disablePortal={false}
                                sx={{ zIndex: 2000 }}
                                onClick={(e) => e.stopPropagation()}
                            >
                                {({ TransitionProps }) => (
                                    <Grow {...TransitionProps}>
                                        <Paper
                                            sx={{
                                                mt: 1,
                                                p: 2,
                                                borderRadius: 1,
                                                boxShadow: "0px 4px 20px rgba(7, 7, 7, 0.08)",
                                                width: 300,
                                            }}
                                        >
                                            <ClickAwayListener onClickAway={handleClose}>
                                                <Box>
                                                    <Grid container spacing={2}>

                                                        <Grid size={12}>
                                                            <Typography sx={{
                                                                fontFamily: 'Outfit',
                                                                fontWeight: 500,
                                                                fontSize: '16px',
                                                                lineHeight: '100%',
                                                                letterSpacing: "0px",
                                                                mb: 1
                                                            }}>Project Name</Typography>
                                                            <TextField
                                                                fullWidth
                                                                name="ProjectName"
                                                                value={filters.ProjectName}
                                                                onChange={handleInputChange}
                                                                placeholder="Enter project name"
                                                                InputProps={{
                                                                    sx: {
                                                                        height: "40px",
                                                                        borderRadius: "8px",
                                                                        backgroundColor: "#fff",
                                                                        "& input::placeholder": {
                                                                            fontWeight: 400,
                                                                            fontSize: "16px",
                                                                            lineHeight: "100%",
                                                                            letterSpacing: "0.16px",
                                                                            color: "#0A0A0C",
                                                                            fontFamily: "Outfit, sans-serif",
                                                                            opacity: 1,
                                                                        },
                                                                        "& input": {
                                                                            fontWeight: 400,
                                                                            fontSize: "16px",
                                                                            lineHeight: "100%",
                                                                            letterSpacing: "0.16px",
                                                                            color: "#0A0A0C",
                                                                            fontFamily: "Outfit, sans-serif",
                                                                        },
                                                                        "& input:-webkit-autofill": {
                                                                            WebkitBoxShadow: "0 0 0 100px #FFFFFF inset !important",
                                                                            WebkitTextFillColor: "#0A0A0C !important",
                                                                            borderRadius: "8px",
                                                                        },

                                                                        "& input:-webkit-autofill:hover": {
                                                                            WebkitBoxShadow: "0 0 0 100px #FFFFFF inset !important",
                                                                        },

                                                                        "& input:-webkit-autofill:focus": {
                                                                            WebkitBoxShadow: "0 0 0 100px #FFFFFF inset !important",
                                                                        },
                                                                    },
                                                                }}
                                                            />
                                                        </Grid>


                                                        <Grid size={12}>
                                                            <Typography sx={{
                                                                fontFamily: 'Outfit',
                                                                fontWeight: 500,
                                                                fontSize: '16px',
                                                                lineHeight: '100%',
                                                                letterSpacing: "0px",
                                                                mb: 1
                                                            }}>Project Type</Typography>
                                                            <Select
                                                                name="ProjectType"
                                                                options={projectType}
                                                                placeholder="Select ProjectType"
                                                                onChange={(option) => handleSelectChange(option, "ProjectType")}
                                                                value={filters.ProjectType ? projectType.find(option => option.value === filters.ProjectType) : null}
                                                            />

                                                        </Grid>

                                                        <Grid size={12}>
                                                            <Typography sx={{
                                                                fontFamily: 'Outfit',
                                                                fontWeight: 500,
                                                                fontSize: '16px',
                                                                lineHeight: '100%',
                                                                letterSpacing: "0px",
                                                                mb: 1
                                                            }}>Admin</Typography>
                                                            <TextField
                                                                fullWidth
                                                                name="Admin"
                                                                value={filters.Admin}
                                                                onChange={handleInputChange}
                                                                placeholder="Enter Admin"
                                                                InputProps={{
                                                                    sx: {
                                                                        height: "40px",
                                                                        borderRadius: "8px",
                                                                        backgroundColor: "#fff",
                                                                        "& input::placeholder": {
                                                                            fontWeight: 400,
                                                                            fontSize: "16px",
                                                                            lineHeight: "100%",
                                                                            letterSpacing: "0.16px",
                                                                            color: "#0A0A0C",
                                                                            fontFamily: "Outfit, sans-serif",
                                                                            opacity: 1,
                                                                        },
                                                                        "& input:-webkit-autofill": {
                                                                            WebkitBoxShadow: "0 0 0 100px #FFFFFF inset !important",
                                                                            WebkitTextFillColor: "#0A0A0C !important",
                                                                            borderRadius: "8px",
                                                                        },

                                                                        "& input:-webkit-autofill:hover": {
                                                                            WebkitBoxShadow: "0 0 0 100px #FFFFFF inset !important",
                                                                        },

                                                                        "& input:-webkit-autofill:focus": {
                                                                            WebkitBoxShadow: "0 0 0 100px #FFFFFF inset !important",
                                                                        },
                                                                    },
                                                                }}
                                                            />
                                                        </Grid>

                                                        <Grid size={12}>
                                                            <Box
                                                                sx={{
                                                                    display: "flex",
                                                                    justifyContent: "flex-end",
                                                                    alignItems: "center",
                                                                }}
                                                            >
                                                                <Button onClick={handleReset}
                                                                    sx={{
                                                                        textTransform: "none",
                                                                        color: "#F15744",
                                                                        fontSize: "14px",
                                                                        lineHeight: "100%",
                                                                        fontFamily: "Outfit, sans-serif",
                                                                        borderRadius: "25px",
                                                                        fontWeight: 500,
                                                                        px: "20px",
                                                                        py: "15px",
                                                                    }}>
                                                                    Reset
                                                                </Button>
                                                                <Button
                                                                    type="button"
                                                                    onClick={handleApplyFilters}
                                                                    variant="contained"
                                                                    sx={{
                                                                        backgroundColor: "#00344C",
                                                                        borderRadius: "25px",
                                                                        textTransform: "none",
                                                                        fontWeight: 500,
                                                                        fontSize: "14px",
                                                                        lineHeight: "100%",
                                                                        fontFamily: "Outfit, sans-serif",
                                                                        color: "#FFFFFF",
                                                                        px: "20px",
                                                                        py: "15px",
                                                                    }}
                                                                >
                                                                    Apply
                                                                </Button>
                                                            </Box>
                                                        </Grid>
                                                    </Grid>
                                                </Box>
                                            </ClickAwayListener>
                                        </Paper>
                                    </Grow>
                                )}
                            </Popper>


                            {/* <IconButton>
                                <BiListUl size={24} color="#48535B" />
                            </IconButton> */}
                            <IconButton>
                                <MdOutlineFileDownload size={24} color="#48535B" />
                            </IconButton>
                            <IconButton>
                                <LuRefreshCw size={20} color="#48535B" />
                            </IconButton>
                        </Box>
                    </Grid>
                </Grid>
            </Grid>


            <TableComponent
                data={paginatedData}
                isLoading={loading}
                columns={columns}
                order={order}
                orderBy={orderBy}
                createSortHandler={createSortHandler}
            />


            <CustomPagination
                totalCount={totalCount}
                pageSize={pageSize}
                page={currentPage}
                onPageChange={handlePageChange}
                setCurrentPage={setCurrentPage}
                onPageSizeChange={handlePageSizeChange}
            />
        </>
    );
}

export default AddedProjects;